import { Navigation } from 'lucide-react';
import { AppLink } from '@/shared/components/AppLink';
import { useOpenWebViewLink } from '@/core/hooks/useOpenWebViewLink';
import { facebookFindLocation } from '../analytics/facebookFindLocation';
import { getStoreDisplayName } from '../../utils/storeListUtils';
import { buildMapDirectionsUrl, buildMapEmbedUrl, usesGooglePlaceEmbedApi } from '../utils/mapEmbed';
import type { PhysicalStoreVm } from '../viewModels/physicalStore.vm';
import { StoreMapOverlay } from './StoreMapOverlay';

type StoreMapCardProps = {
  store: PhysicalStoreVm;
};

export function StoreMapCard({ store }: StoreMapCardProps) {
  const { createClickHandler, isApp } = useOpenWebViewLink();
  const displayName = getStoreDisplayName(store.storeName);
  const cityName = store.cityName?.trim() || '';
  const heading = cityName ? `${displayName}, ${cityName}` : displayName;
  const mapEmbedUrl = buildMapEmbedUrl(store);
  const showMapOverlay = mapEmbedUrl != null && !usesGooglePlaceEmbedApi(mapEmbedUrl);
  const contactDigits = store.contactNumber ? store.contactNumber.replace(/\D/g, '') : '';
  const callHref = contactDigits ? `tel:${contactDigits}` : undefined;
  const directionsHref = buildMapDirectionsUrl(store);
  // `storeUrl` is the backend-provided, already-fully-encoded catalog path segment
  // (see `OtherStoresWidget`, which uses the same field). Do NOT rebuild it via
  // `buildStoreCatalogUrl`/`store.sid`: `sid` here is already double-base64-encoded
  // by this API, so re-encoding it through `buildStoreCatalogUrl` triple-encodes it
  // and produces a broken store PLP URL.
  const catalogUrl = `/stores/${store.storeUrl}/catalog.html`;

  const handleDirectionsClick = () => {
    facebookFindLocation(store.storeName, store.id, store.cityName);
  };

  return (
    <article className="mb-8 overflow-hidden rounded border border-gray-200 bg-white shadow-sm">
      {mapEmbedUrl ? (
        <div className="relative h-[280px] w-full bg-gray-100 md:h-[320px]">
          <iframe
            title={`Map for ${displayName}`}
            src={mapEmbedUrl}
            className="h-full w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          {showMapOverlay ? <StoreMapOverlay store={store} /> : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-3 md:items-start md:gap-6 md:px-6 md:py-6">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 md:text-lg">{heading}</h3>
          {store.address ? (
            <p className="mt-1 text-sm text-gray-600">{store.address}</p>
          ) : null}
          <AppLink
            to={catalogUrl}
            useAppBridge
            target={isApp ? undefined : '_blank'}
            className="mt-3 inline-block text-sm font-medium text-[#4A7B9D] hover:underline"
          >
            View all designs in store
          </AppLink>
        </div>

        <div className="space-y-1 text-sm text-gray-600">
          {store.storeTimings ? (
            <p>
              <span className="text-gray-800">Store timings:</span> {store.storeTimings}
            </p>
          ) : null}
          {callHref ? (
            <p>
              <span className="text-gray-800">Contact Number:</span>{' '}
              <a href={callHref} className="text-[#4A7B9D] hover:underline">
                {store.contactNumber}
              </a>
            </p>
          ) : null}
        </div>

        <div className="flex md:justify-end">
          {directionsHref ? (
            <a
              href={directionsHref}
              target={isApp ? undefined : '_blank'}
              rel={isApp ? undefined : 'noreferrer'}
              onClick={(event) => {
                handleDirectionsClick();
                if (isApp) {
                  createClickHandler(directionsHref)(event);
                }
              }}
              className="inline-flex items-center gap-2 rounded bg-[var(--store-plp-top-bar-bg)] px-6 py-2.5 text-xs font-semibold uppercase tracking-wide text-white hover:opacity-90"
            >
              <Navigation className="h-4 w-4" aria-hidden />
              Get Direction
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}
